import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

interface DynamicChartsProps {
  chartType: string;
  query: string;
  dateRange?: string;
}

export function DynamicCharts({ chartType, query, dateRange = "30" }: DynamicChartsProps) {
  const chartConfig = {
    value: { label: "Value", color: "#10b981" },
    roi: { label: "ROI %", color: "#3b82f6" },
    cost: { label: "Cost", color: "#f59e0b" },
    revenue: { label: "Revenue", color: "#10b981" },
    members: { label: "Members", color: "#8b5cf6" },
    points: { label: "Points", color: "#ef4444" },
    sales: { label: "Sales", color: "#059669" },
    transactions: { label: "Transactions", color: "#dc2626" }
  };

  const getChartData = (type: string, days: number) => {
    // Generate data points based on the selected date range
    const generateTimeSeriesData = (baseData: any[], targetDays: number) => {
      if (targetDays <= 7) {
        return baseData.slice(0, 7);
      } else if (targetDays <= 30) {
        return baseData;
      } else if (targetDays <= 60) {
        // Extend data for 60 days by duplicating and modifying
        return [...baseData, ...baseData.map((item, index) => ({
          ...item,
          month: `Month ${index + 7}`,
          roi: item.roi * (0.9 + Math.random() * 0.2),
          cost: item.cost * (0.95 + Math.random() * 0.1),
          revenue: item.revenue * (0.95 + Math.random() * 0.1)
        }))];
      } else {
        // Extend data for longer periods
        const multiplier = Math.ceil(targetDays / 30);
        let extendedData = [];
        for (let i = 0; i < multiplier; i++) {
          extendedData.push(...baseData.map((item, index) => ({
            ...item,
            month: `Period ${i * 6 + index + 1}`,
            roi: item.roi * (0.8 + Math.random() * 0.4),
            cost: item.cost * (0.9 + Math.random() * 0.2),
            revenue: item.revenue * (0.9 + Math.random() * 0.2)
          })));
        }
        return extendedData.slice(0, Math.min(targetDays / 5, 24)); // Limit to reasonable number of points
      }
    };

    const rangeDays = parseInt(days.toString());

    switch (type) {
      case 'roi-trend':
        const roiBaseData = [
          { month: 'Jan', roi: 18.5, cost: 45000, revenue: 195000 },
          { month: 'Feb', roi: 21.2, cost: 52000, revenue: 215000 },
          { month: 'Mar', roi: 19.8, cost: 48000, revenue: 198000 },
          { month: 'Apr', roi: 23.5, cost: 55000, revenue: 235000 },
          { month: 'May', roi: 25.1, cost: 58000, revenue: 248000 },
          { month: 'Jun', roi: 22.9, cost: 51000, revenue: 225000 }
        ];
        return generateTimeSeriesData(roiBaseData, rangeDays);

      case 'campaign-performance':
        const campaignBaseData = [
          { name: 'Summer Sale', performance: 45, engagement: 12.3, cost: 15000 },
          { name: 'Back to School', performance: 32, engagement: 8.7, cost: 12000 },
          { name: 'Holiday Prep', performance: 28, engagement: 6.2, cost: 18000 },
          { name: 'Weekend Special', performance: 25, engagement: 5.8, cost: 8000 },
          { name: 'Flash Friday', performance: 22, engagement: 4.1, cost: 10000 }
        ];
        // For campaign performance, we'll adjust the performance values based on range
        return campaignBaseData.map(item => ({
          ...item,
          performance: rangeDays > 60 ? item.performance * 1.2 : item.performance,
          engagement: rangeDays > 60 ? item.engagement * 1.1 : item.engagement
        }));

      case 'enrollment-trends':
        const enrollmentBaseData = [
          { month: 'Jan', enrolled: 1250, active: 850, rate: 12.5 },
          { month: 'Feb', enrolled: 1380, active: 920, rate: 13.8 },
          { month: 'Mar', enrolled: 1420, active: 980, rate: 14.2 },
          { month: 'Apr', enrolled: 1650, active: 1120, rate: 16.5 },
          { month: 'May', enrolled: 1580, active: 1050, rate: 15.8 },
          { month: 'Jun', enrolled: 1720, active: 1180, rate: 17.2 }
        ];
        return generateTimeSeriesData(enrollmentBaseData, rangeDays);

      case 'redemption-trends':
        const redemptionBaseData = [
          { month: 'Jan', redeemed: 45000, issued: 62000, rate: 72.6 },
          { month: 'Feb', redeemed: 52000, issued: 68000, rate: 76.5 },
          { month: 'Mar', redeemed: 48000, issued: 65000, rate: 73.8 },
          { month: 'Apr', redeemed: 55000, issued: 72000, rate: 76.4 },
          { month: 'May', redeemed: 58000, issued: 75000, rate: 77.3 },
          { month: 'Jun', redeemed: 61000, issued: 80000, rate: 76.3 }
        ];
        return generateTimeSeriesData(redemptionBaseData, rangeDays);

      case 'cost-breakdown':
        const costBaseData = [
          { name: 'Point Issuance', value: 35, amount: 175000 },
          { name: 'Redemption Processing', value: 25, amount: 125000 },
          { name: 'Technology Platform', value: 20, amount: 100000 },
          { name: 'Customer Support', value: 12, amount: 60000 },
          { name: 'Marketing & Promotions', value: 8, amount: 40000 }
        ];
        // Adjust cost breakdown based on range (longer periods = higher costs)
        const costMultiplier = rangeDays > 30 ? 1 + (rangeDays - 30) / 100 : 1;
        return costBaseData.map(item => ({
          ...item,
          amount: Math.round(item.amount * costMultiplier)
        }));

      case 'merchant-sales':
        const merchantSalesData = [
          { month: 'Jan', sales: 145000, transactions: 1250 },
          { month: 'Feb', sales: 162000, transactions: 1380 },
          { month: 'Mar', sales: 158000, transactions: 1420 },
          { month: 'Apr', sales: 185000, transactions: 1650 },
          { month: 'May', sales: 178000, transactions: 1580 },
          { month: 'Jun', sales: 198000, transactions: 1720 }
        ];
        return generateTimeSeriesData(merchantSalesData, rangeDays);

      case 'points-issued':
        const pointsIssuedData = [
          { month: 'Jan', issued: 72500, redeemed: 58000 },
          { month: 'Feb', issued: 81000, redeemed: 65000 },
          { month: 'Mar', issued: 79000, redeemed: 63000 },
          { month: 'Apr', issued: 92500, redeemed: 74000 },
          { month: 'May', issued: 89000, redeemed: 71000 },
          { month: 'Jun', issued: 99000, redeemed: 79000 }
        ];
        return generateTimeSeriesData(pointsIssuedData, rangeDays);

      case 'merchant-performance':
        const merchantPerformanceData = [
          { name: 'Carrefour', sales: 485000, transactions: 4200, category: 'Retail' },
          { name: 'Mall of Emirates', sales: 298000, transactions: 2100, category: 'Shopping' },
          { name: 'Starbucks', sales: 187000, transactions: 3800, category: 'F&B' },
          { name: 'Vox Cinemas', sales: 156000, transactions: 1200, category: 'Entertainment' },
          { name: 'Adidas', sales: 134000, transactions: 900, category: 'Retail' }
        ];
        return merchantPerformanceData.map(item => ({
          ...item,
          sales: rangeDays > 60 ? item.sales * 1.2 : item.sales
        }));

      case 'transaction-volume':
        const transactionVolumeData = [
          { month: 'Jan', volume: 12500, value: 1450000 },
          { month: 'Feb', volume: 13800, value: 1620000 },
          { month: 'Mar', volume: 14200, value: 1580000 },
          { month: 'Apr', volume: 16500, value: 1850000 },
          { month: 'May', volume: 15800, value: 1780000 },
          { month: 'Jun', volume: 17200, value: 1980000 }
        ];
        return generateTimeSeriesData(transactionVolumeData, rangeDays);

      case 'category-breakdown':
        const categoryBreakdownData = [
          { name: 'Retail', value: 45, amount: 847000 },
          { name: 'F&B', value: 28, amount: 523000 },
          { name: 'Entertainment', value: 17, amount: 312000 },
          { name: 'Travel', value: 6, amount: 115000 },
          { name: 'Services', value: 4, amount: 78000 }
        ];
        const categoryMultiplier = rangeDays > 30 ? 1 + (rangeDays - 30) / 100 : 1;
        return categoryBreakdownData.map(item => ({
          ...item,
          amount: Math.round(item.amount * categoryMultiplier)
        }));

      case 'merchant-growth':
        const merchantGrowthData = [
          { month: 'Jan', newMerchants: 8, totalMerchants: 142, growth: 5.6 },
          { month: 'Feb', newMerchants: 12, totalMerchants: 154, growth: 8.5 },
          { month: 'Mar', newMerchants: 6, totalMerchants: 160, growth: 3.9 },
          { month: 'Apr', newMerchants: 15, totalMerchants: 175, growth: 9.4 },
          { month: 'May', newMerchants: 9, totalMerchants: 184, growth: 5.1 },
          { month: 'Jun', newMerchants: 11, totalMerchants: 195, growth: 6.0 }
        ];
        return generateTimeSeriesData(merchantGrowthData, rangeDays);

      default:
        return [];
    }
  };

  const renderChart = () => {
    const data = getChartData(chartType, parseInt(dateRange));
    
    switch (chartType) {
      case 'roi-trend':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="roi" stroke="var(--color-roi)" strokeWidth={2} />
            </LineChart>
          </ChartContainer>
        );
      
      case 'campaign-performance':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="performance" fill="var(--color-value)" />
            </BarChart>
          </ChartContainer>
        );
      
      case 'enrollment-trends':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="enrolled" stroke="var(--color-members)" strokeWidth={2} />
              <Line type="monotone" dataKey="active" stroke="var(--color-value)" strokeWidth={2} />
            </LineChart>
          </ChartContainer>
        );
      
      case 'redemption-trends':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="redeemed" fill="var(--color-points)" />
              <Bar dataKey="issued" fill="var(--color-value)" />
            </BarChart>
          </ChartContainer>
        );
      
      case 'cost-breakdown':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent />} />
            </PieChart>
          </ChartContainer>
        );
      
      case 'merchant-sales':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="sales" stroke="var(--color-sales)" strokeWidth={2} />
            </LineChart>
          </ChartContainer>
        );

      case 'points-issued':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="issued" fill="var(--color-points)" />
              <Bar dataKey="redeemed" fill="var(--color-value)" />
            </BarChart>
          </ChartContainer>
        );

      case 'merchant-performance':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="sales" fill="var(--color-sales)" />
            </BarChart>
          </ChartContainer>
        );

      case 'transaction-volume':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="volume" fill="var(--color-transactions)" />
            </BarChart>
          </ChartContainer>
        );

      case 'category-breakdown':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 72}, 70%, 50%)`} />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent />} />
            </PieChart>
          </ChartContainer>
        );

      case 'merchant-growth':
        return (
          <ChartContainer config={chartConfig} className="h-[300px]">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="newMerchants" stroke="var(--color-value)" strokeWidth={2} />
              <Line type="monotone" dataKey="growth" stroke="var(--color-roi)" strokeWidth={2} />
            </LineChart>
          </ChartContainer>
        );
      
      default:
        return (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <p>Chart will be generated based on your query</p>
          </div>
        );
    }
  };

  const getChartTitle = () => {
    const rangeDays = parseInt(dateRange);
    const rangeText = rangeDays <= 7 ? "Weekly" : rangeDays <= 30 ? "Monthly" : rangeDays <= 90 ? "Quarterly" : "Annual";
    
    switch (chartType) {
      case 'roi-trend':
        return `${rangeText} Program ROI Trend`;
      case 'campaign-performance':
        return `${rangeText} Campaign Performance Analysis`;
      case 'enrollment-trends':
        return `${rangeText} Member Enrollment Trends`;
      case 'redemption-trends':
        return `${rangeText} Points Redemption Analysis`;
      case 'cost-breakdown':
        return `${rangeText} Program Cost Breakdown`;
      case 'merchant-sales':
        return `${rangeText} Merchant Sales Overview`;
      case 'points-issued':
        return `${rangeText} Points Issued by Merchants`;
      case 'merchant-performance':
        return `${rangeText} Top Performing Merchants`;
      case 'transaction-volume':
        return `${rangeText} Transaction Volume Analysis`;
      case 'category-breakdown':
        return `${rangeText} Sales by Category`;
      case 'merchant-growth':
        return `${rangeText} Merchant Growth Trends`;
      default:
        return 'Generated Chart';
    }
  };

  if (!chartType) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{getChartTitle()}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Based on query: "{query}" • Last {dateRange} days
        </p>
      </CardHeader>
      <CardContent>
        {renderChart()}
      </CardContent>
    </Card>
  );
}
